import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import api from '../../utils/axiosConfig';
import {
    CurrencyDollarIcon,
    ChartBarIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    CalendarIcon,
    BanknotesIcon,
    ClockIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';

interface EarningsStats {
    totalEarnings: number;
    verifiedEarnings: number;
    pendingEarnings: number;
    totalWithdrawn: number;
    pendingWithdrawals: number;
    totalUsers: number;
    activeUsers: number;
    totalTasks: number;
    completedTasks: number;
    earningsGrowth: number;
    withdrawalGrowth: number;
    adminEarnings: number;
}

interface UserEarnings {
    _id: string;
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        designation: string;
    };
    currentBalance: number;
    verifiedBalance: number;
    pendingBalance: number;
    totalEarnings: number;
    totalWithdrawn: number;
    availableForWithdrawal: number;
    totalPendingWithdrawals: number;
    totalApprovedWithdrawals: number;
    taskCount: number;
    lastActivity: string;
}

interface TaskEarnings {
    _id: string;
    title: string;
    company: {
        companyName: string;
    } | null;
    totalEarnings: number;
    verifiedEarnings: number;
    pendingEarnings: number;
    totalDataEntries: number;
    verifiedDataEntries: number;
    pendingDataEntries: number;
    budgetPerEntry: number;
    userCount: number;
    completedTasks: number;
    totalTasks: number;
    status: string;
    createdAt: string;
}

const AdminEarnings: React.FC = () => {
    const dispatch = useAppDispatch();
    const [stats, setStats] = useState<EarningsStats | null>(null);
    const [userEarnings, setUserEarnings] = useState<UserEarnings[]>([]);
    const [taskEarnings, setTaskEarnings] = useState<TaskEarnings[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'tasks'>('overview');
    const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

    const fetchEarningsData = async () => {
        try {
            setLoading(true);
            const [statsResponse, usersResponse, tasksResponse] = await Promise.all([
                api.get(`/admin/earnings/stats?range=${dateRange}`),
                api.get(`/admin/earnings/users?range=${dateRange}`),
                api.get(`/admin/earnings/tasks?range=${dateRange}`)
            ]);

            if (statsResponse.data.success) {
                setStats(statsResponse.data.data);
            }
            if (usersResponse.data.success) {
                setUserEarnings(usersResponse.data.data);
            }
            if (tasksResponse.data.success) {
                setTaskEarnings(tasksResponse.data.data);
            }
        } catch (error: any) {
            console.error('Error fetching earnings data:', error);
            dispatch(showToast({
                message: error.response?.data?.message || 'Failed to fetch earnings data',
                type: 'error'
            }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEarningsData();
    }, [dateRange]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400 text-lg">Loading earnings data...</p>
                </div>
            </div>
        );
    }

    const formatCurrency = (amount: number) => `₹${amount?.toLocaleString() || '0'}`;
    const formatPercentage = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-xl">
                                <CurrencyDollarIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Earnings Dashboard</h1>
                                <p className="text-gray-600 dark:text-gray-400">System-wide earnings and financial overview</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <select
                                value={dateRange}
                                onChange={(e) => setDateRange(e.target.value as any)}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="7d">Last 7 days</option>
                                <option value="30d">Last 30 days</option>
                                <option value="90d">Last 90 days</option>
                                <option value="all">All time</option>
                            </select>
                            <button
                                onClick={fetchEarningsData}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                            >
                                <ChartBarIcon className="h-4 w-4" />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
                    <nav className="flex space-x-1 p-2">
                        {[
                            { id: 'overview', label: 'Overview', icon: ChartBarIcon },
                            { id: 'users', label: 'User Earnings', icon: UserGroupIcon },
                            { id: 'tasks', label: 'Task Earnings', icon: BuildingOfficeIcon },
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${activeTab === tab.id
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                            >
                                <tab.icon className="h-5 w-5" />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && stats && (
                    <div className="space-y-8">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-6 rounded-2xl shadow-sm border border-green-200 dark:border-green-800">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-green-100 dark:bg-green-800/30 rounded-xl">
                                        <CurrencyDollarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <ArrowTrendingUpIcon className="h-4 w-4 text-green-600" />
                                        <span className="text-sm font-medium text-green-600">{formatPercentage(stats.earningsGrowth)}</span>
                                    </div>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Total Earnings</h3>
                                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{formatCurrency(stats.totalEarnings)}</p>
                                <p className="text-sm text-green-600/70 dark:text-green-400/70 mt-1">All user earnings</p>
                            </div>

                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-6 rounded-2xl shadow-sm border border-blue-200 dark:border-blue-800">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-blue-100 dark:bg-blue-800/30 rounded-xl">
                                        <CheckCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Verified</span>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Verified Earnings</h3>
                                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(stats.verifiedEarnings)}</p>
                                <p className="text-sm text-blue-600/70 dark:text-blue-400/70 mt-1">Validated earnings</p>
                            </div>

                            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-6 rounded-2xl shadow-sm border border-yellow-200 dark:border-yellow-800">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-yellow-100 dark:bg-yellow-800/30 rounded-xl">
                                        <ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                                    </div>
                                    <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Pending</span>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Pending Earnings</h3>
                                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{formatCurrency(stats.pendingEarnings)}</p>
                                <p className="text-sm text-yellow-600/70 dark:text-yellow-400/70 mt-1">Awaiting validation</p>
                            </div>

                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-6 rounded-2xl shadow-sm border border-purple-200 dark:border-purple-800">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-purple-100 dark:bg-purple-800/30 rounded-xl">
                                        <BanknotesIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <ArrowTrendingUpIcon className="h-4 w-4 text-purple-600" />
                                        <span className="text-sm font-medium text-purple-600">{formatPercentage(stats.withdrawalGrowth)}</span>
                                    </div>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Total Withdrawn</h3>
                                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(stats.totalWithdrawn)}</p>
                                <p className="text-sm text-purple-600/70 dark:text-purple-400/70 mt-1">Processed withdrawals</p>
                            </div>

                            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 p-6 rounded-2xl shadow-sm border border-indigo-200 dark:border-indigo-800">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 bg-indigo-100 dark:bg-indigo-800/30 rounded-xl">
                                        <UserGroupIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                                    </div>
                                    <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">Admin</span>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Admin Earnings</h3>
                                <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(stats.adminEarnings)}</p>
                                <p className="text-sm text-indigo-600/70 dark:text-indigo-400/70 mt-1">Administrator earnings</p>
                            </div>
                        </div>

                        {/* User and Task Statistics */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                        <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User Statistics</h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">User engagement and activity</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Total Users:</span>
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.totalUsers}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Active Users:</span>
                                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">{stats.activeUsers}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Active Rate:</span>
                                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                            {stats.totalUsers > 0 ? ((stats.activeUsers / stats.totalUsers) * 100).toFixed(1) : '0.0'}%
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                        <BuildingOfficeIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Task Statistics</h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Task completion and performance</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Total Tasks:</span>
                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.totalTasks}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Completed Tasks:</span>
                                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">{stats.completedTasks}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Completion Rate:</span>
                                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                            {stats.totalTasks > 0 ? ((stats.completedTasks / stats.totalTasks) * 100).toFixed(1) : '0.0'}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* User Earnings Tab */}
                {activeTab === 'users' && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                    <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">User Earnings</h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Individual user earnings breakdown</p>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            User
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Designation
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Available for Withdrawal
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Verified Balance
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Pending Balance
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Total Earnings
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Total Withdrawn
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Pending Withdrawals
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Tasks
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Last Activity
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {userEarnings.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                                No user earnings data available
                                            </td>
                                        </tr>
                                    ) : (
                                        userEarnings.map((user) => (
                                            <tr key={user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                            {user.user.firstName} {user.user.lastName}
                                                        </div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.user.email}</div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full">
                                                        {user.user.designation?.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600 dark:text-green-400">
                                                    {formatCurrency(user.availableForWithdrawal)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600 dark:text-blue-400">
                                                    {formatCurrency(user.verifiedBalance)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                                                    {formatCurrency(user.pendingBalance)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600 dark:text-purple-400">
                                                    {formatCurrency(user.totalEarnings)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600 dark:text-red-400">
                                                    {formatCurrency(user.totalWithdrawn)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-orange-600 dark:text-orange-400">
                                                    {formatCurrency(user.totalPendingWithdrawals)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {user.taskCount}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    <div className="flex items-center gap-1">
                                                        <CalendarIcon className="h-4 w-4" />
                                                        {new Date(user.lastActivity).toLocaleDateString()}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Task Earnings Tab */}
                {activeTab === 'tasks' && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                                    <BuildingOfficeIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Task Earnings</h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Earnings breakdown by task</p>
                                </div>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Task
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Company
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Budget/Entry
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Data Entries
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Verified Earnings
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Pending Earnings
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Total Earnings
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Users
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Created
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {taskEarnings.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                                                No task earnings data available
                                            </td>
                                        </tr>
                                    ) : (
                                        taskEarnings.map((task) => (
                                            <tr key={task._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                        {task.title || 'Untitled Task'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {task.company?.companyName || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600 dark:text-blue-400">
                                                    {formatCurrency(task.budgetPerEntry)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    <div className="flex flex-col">
                                                        <span className="font-semibold">{task.totalDataEntries}</span>
                                                        <span className="text-xs text-gray-500">
                                                            V: {task.verifiedDataEntries} | P: {task.pendingDataEntries}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600 dark:text-green-400">
                                                    {formatCurrency(task.verifiedEarnings)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                                                    {formatCurrency(task.pendingEarnings)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600 dark:text-purple-400">
                                                    {formatCurrency(task.totalEarnings)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                    {task.userCount}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${task.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                                        task.status === 'completed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                                                            'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                                                        }`}>
                                                        {task.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    <div className="flex items-center gap-1">
                                                        <CalendarIcon className="h-4 w-4" />
                                                        {new Date(task.createdAt).toLocaleDateString()}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminEarnings;
